import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  CONTRACT_STATUSES,
  CURRENCY_CATALOGUE,
  METAFIELD_ENTITIES_FOR,
  REPORT_COLUMNS,
  REPORT_FILTER_NONE,
  formatMoney,
  listSpecOf,
  metafieldColumnKey,
  reportListResultSchema,
  type Client,
  type Contract,
  type LineItem,
  type Metafield,
  type Person,
  type Project,
  type ReportDefinition,
  type ReportFilterKey,
  type ReportListColumn,
  type ReportListResult,
  type TaskPriority,
  type TaskStatus,
} from '@forecastify/shared';
import {
  DataList,
  ListToolbar,
  type DataListColumn,
  type FilterOption,
  type ToolbarAction,
} from '@forecastify/ui';
import { useDuration, type Duration } from '../../duration';
import { useI18n, useT, type Locale, type Translate } from '../../i18n';
import { useSession } from '../../session';
import { REPORT_LIST_TOPICS } from '../../live/topics';
import { useLiveInvalidation } from '../../live/use-live';
import { listLabels } from '../delivery/clients-screen';
import { envelopeOf, messageFor } from './report-errors';
import { formatCalendarDate, formatInstant } from '../history/history-labels';
import { usePages } from '../use-pages';
import { useWorkspaceNavigate } from '../../workspace-path';
import {
  contractOptions,
  lineItemOptionLabel,
  repeatedContractNames,
} from '../contracts/contract-label';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ 16 Oct 2026 · A LIST REPORT — one row per record, the columns chosen.     │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * ⚠️⚠️ **THE TOOLBAR IS THE REPORT'S, NOT THE LIST'S.** Which columns, in which order, which
 * filters and which sort are part of the DEFINITION — a saved list report reopens with them. So
 * the toolbar here is controlled by the definition and `DataList` draws without its own
 * (`toolbar={false}`), which would otherwise keep a second column choice in `localStorage`.
 *
 * ⚠️ **Nothing is stored** (`0219`): every page is a read of live data, so the view subscribes to
 * `REPORT_LIST_TOPICS` and re-reads when any of it moves.
 *
 * ⚠️ **No metric, no dimension, no chart.** Those shape a summary; a list is its columns.
 */

export interface ReportListOptions {
  readonly projects: readonly Project[];
  readonly people: readonly Person[];
  readonly clients: readonly Client[];
  readonly contracts: readonly Contract[];
  readonly lineItems: readonly LineItem[];
  readonly statuses: readonly TaskStatus[];
  readonly priorities: readonly TaskPriority[];
  /** R-60 · the workspace's teams, for the *Csapat* filter. */
  readonly teams: readonly { readonly id: string; readonly name: string }[];
  /** Every metafield of the workspace — the column picker offers those this source reaches. */
  readonly metafields: readonly Metafield[];
}

export function ReportListView({
  definition,
  options,
  canMoney,
  reloadKey,
  onDefinition,
  onExport,
}: {
  readonly definition: ReportDefinition;
  readonly options: ReportListOptions;
  readonly canMoney: boolean;
  /** Bumped by the header's Refresh, so a saved list re-reads on request too. */
  readonly reloadKey: number;
  readonly onDefinition: (patch: Partial<ReportDefinition>) => void;
  /** Null for an unsaved report — the export reads a SAVED definition. */
  readonly onExport: (() => void) | null;
}) {
  const t = useT();
  const { fmt } = useDuration();
  const { locale } = useI18n();
  const session = useSession();
  const go = useWorkspaceNavigate();
  const pages = usePages();
  const spec = listSpecOf(definition);

  const [result, setResult] = useState<ReportListResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  /* ⚠️ The search box is typed into; the definition follows a beat later, so a word is one read. */
  const [search, setSearch] = useState(spec.filters.search ?? '');

  useEffect(() => {
    const timer = setTimeout(() => {
      if ((spec.filters.search ?? '') !== search) {
        onDefinition({ filters: { ...spec.filters, search: search === '' ? undefined : search } });
      }
    }, 300);
    return () => {
      clearTimeout(timer);
    };
  }, [search, spec.filters, onDefinition]);

  const definitionKey = JSON.stringify(definition);
  const { offset, limit, settle, reset } = pages;

  /* A changed definition starts at page one — page seven of a different question is nothing. */
  useEffect(() => {
    reset();
  }, [definitionKey, reset]);

  const load = useCallback(async () => {
    const response = await session
      .request('/reports/rows', {
        method: 'POST',
        body: JSON.stringify({ definition: JSON.parse(definitionKey) as unknown, offset, limit }),
      })
      .catch(() => null);
    setLoading(false);
    if (response === null) {
      setError(t('error.unavailable'));
      return;
    }
    if (!response.ok) {
      const { code, detail } = await envelopeOf(response);
      setError(
        code === 'money_reports_not_allowed'
          ? t('report.money.lockedNote')
          : messageFor(t, code, detail),
      );
      return;
    }
    const parsed = reportListResultSchema.safeParse(await response.json());
    if (!parsed.success) {
      setError(t('error.unavailable'));
      return;
    }
    setError(null);
    setResult(parsed.data);
    settle(parsed.data.meta.total);
  }, [session, definitionKey, offset, limit, settle, t]);

  useEffect(() => {
    void load();
  }, [load, reloadKey]);

  useLiveInvalidation(REPORT_LIST_TOPICS, load);

  /* ── the column picker: everything the source can show ──────────────────────────────────── */

  const available = useMemo(() => {
    const fixed = REPORT_COLUMNS[definition.source]
      .filter((column) => canMoney || column.money !== true)
      .map((column) => ({ key: column.key, label: reportColumnLabel(t, column.key) }));
    const entities = METAFIELD_ENTITIES_FOR[definition.source];
    const fields = options.metafields
      .filter((field) => entities.includes(field.entity))
      .map((field) => ({ key: metafieldColumnKey(field.id), label: field.name }));
    return [...fixed, ...fields];
  }, [definition.source, canMoney, options.metafields, t]);

  /* Chosen ones first, in their order; the rest after, in the catalogue's. */
  const pickerColumns = [
    ...spec.columns
      .map((key) => available.find((column) => column.key === key))
      .filter((column): column is { key: string; label: string } => column !== undefined),
    ...available.filter((column) => !spec.columns.includes(column.key)),
  ];

  const actions: ToolbarAction[] =
    onExport === null
      ? []
      : [{ label: t('report.action.export'), icon: 'download', onSelect: onExport }];

  /* ── the rows ───────────────────────────────────────────────────────────────────────────── */

  const wireColumns: readonly ReportListColumn[] = result?.columns ?? [];
  const currency = CURRENCY_CATALOGUE.find((one) => one.code === result?.currencyCode) ?? {
    code: result?.currencyCode ?? 'HUF',
    symbol: result?.currencyCode ?? 'Ft',
  };

  const columns: DataListColumn[] = wireColumns.map((column) => ({
    key: column.key,
    label: column.label ?? reportColumnLabel(t, column.key),
    align: ['hours', 'money', 'number'].includes(column.kind) ? 'right' : 'left',
    ...(column.sortable ? { sortKey: column.key } : {}),
  }));

  const rows = (result?.data ?? []).map((row) => ({
    id: row.id,
    cells: Object.fromEntries(
      wireColumns.map((column, index) => [
        column.key,
        cellText(t, locale, fmt, column, row.cells[index] ?? null, currency),
      ]),
    ),
  }));
  const pathOf = new Map((result?.data ?? []).map((row) => [row.id, row.path]));

  return (
    /* ⚠️ A bare flex column, and the list owns its own padding — `project-screen.tsx` records the
       shape. `DataList` scrolls inside it. */
    <div className="flex min-h-0 flex-1 flex-col" data-screen-part="report-list">
      <ListToolbar
        search={search}
        onSearchChange={setSearch}
        /* ⚠️ No filters here: they live in the builder's rail, for both layouts (16 Oct 2026). */
        columns={pickerColumns}
        hidden={pickerColumns
          .filter((one) => !spec.columns.includes(one.key))
          .map((one) => one.key)}
        onToggleColumn={(key) => {
          /* ⚠️ The last column cannot go — a list of nothing is refused by the schema. */
          if (spec.columns.includes(key)) {
            if (spec.columns.length === 1) return;
            onDefinition({
              columns: spec.columns.filter((one) => one !== key),
              ...(spec.sort?.column === key ? { sort: null } : {}),
            });
          } else {
            onDefinition({ columns: [...spec.columns, key] });
          }
        }}
        onMoveColumn={(key, to) => {
          /* Only a chosen column moves, and only among the chosen ones. */
          if (!spec.columns.includes(key)) return;
          const without = spec.columns.filter((one) => one !== key);
          const at = Math.min(Math.max(0, to), without.length);
          onDefinition({ columns: [...without.slice(0, at), key, ...without.slice(at)] });
        }}
        actions={actions}
        labels={listLabels(t)}
      />

      {error !== null && (
        <p
          role="alert"
          className="border-destructive/40 bg-destructive/10 text-destructive mx-4 my-2 rounded-md border px-3 py-2 text-[12.5px]"
        >
          {error}
        </p>
      )}

      <DataList
        toolbar={false}
        selectable={false}
        columns={columns}
        rows={rows}
        labels={listLabels(t)}
        isLoading={loading}
        sort={spec.sort === null ? [] : [{ key: spec.sort.column, direction: spec.sort.direction }]}
        onSortChange={(next) => {
          const first = next[0];
          onDefinition({
            sort: first === undefined ? null : { column: first.key, direction: first.direction },
          });
        }}
        onRowClick={(id) => {
          const path = pathOf.get(id);
          if (path !== null && path !== undefined) go(path);
        }}
        empty={{ title: t('report.list.empty'), body: t('report.list.emptyBody') }}
        page={{
          hasPrevious: pages.hasPrevious,
          hasNext: pages.hasNext,
          onPrevious: pages.previous,
          onNext: pages.next,
          index: pages.index,
          count: pages.count,
          total: pages.total,
          onGoTo: pages.goTo,
          pageSize: pages.limit,
        }}
      />
    </div>
  );
}

/**
 * ⚠️⚠️ 16 Oct 2026 · **THE FILTER VOCABULARY, FOR THE BUILDER'S RAIL.** The list once drew these in
 * its own toolbar beside a rail whose filters were disabled — two places for one question. The rail
 * holds them now, for the summary and the list alike, and reads its options from here.
 */
export function filterOptions(
  t: Translate,
  key: ReportFilterKey,
  source: ReportDefinition['source'],
  options: ReportListOptions,
): FilterOption[] {
  /**
   * ⚠️ 17 Oct 2026 · ***None* first, on a worklog or task report** (`REPORT_FILTER_NONE`) — the
   * hours or tasks with no project, client, contract or line item. *Nincs sortétel* is
   * Kockázatkezelés's unknown set.
   */
  const none = (label: string): FilterOption[] =>
    source === 'worklog' || source === 'task' ? [{ value: REPORT_FILTER_NONE, label }] : [];
  if (key === 'projects') {
    return [
      ...none(t('report.group.noProject')),
      ...options.projects.map((one) => ({ value: one.id, label: one.name })),
    ];
  }
  if (key === 'clients') {
    return [
      ...none(t('report.group.noClient')),
      ...options.clients.map((one) => ({ value: one.id, label: one.name })),
    ];
  }
  if (key === 'members') {
    return options.people.map((one) => ({ value: one.id, label: one.displayName }));
  }
  if (key === 'teams') {
    return options.teams.map((one) => ({ value: one.id, label: one.name }));
  }
  if (key === 'contracts') {
    return [
      ...none(t('report.group.noContract')),
      /* 3 Nov 2026 · R-57 · never the human code: the name, the identifier beside it. */
      ...contractOptions(options.contracts),
    ];
  }
  if (key === 'lineItems') {
    const repeated = repeatedContractNames(options.lineItems);
    return [
      ...none(t('report.group.noLineItem')),
      ...options.lineItems.map((one) => ({
        value: one.id,
        label: lineItemOptionLabel(one, repeated),
        hint: one.identifier,
      })),
    ];
  }
  if (key === 'priorities') {
    return options.priorities.map((one) => ({ value: one.id, label: one.name }));
  }
  /* ⚠️ A status is a task status on the task-based sources and the contract's own enum otherwise. */
  return source === 'worklog' || source === 'task'
    ? options.statuses.map((one) => ({ value: one.id, label: one.name }))
    : CONTRACT_STATUSES.map((status) => ({ value: status, label: contractStatusLabel(t, status) }));
}

/** One cell as text, by the column's kind. */
function cellText(
  t: Translate,
  locale: Locale,
  fmt: Duration['fmt'],
  column: ReportListColumn,
  value: string | null,
  currency: { code: string; symbol: string },
): string {
  if (value === null || value === '') return '–';
  switch (column.kind) {
    case 'hours':
      return fmt(value);
    case 'money':
      return formatMoney(Number(value), currency);
    case 'date':
      return formatCalendarDate(value, locale);
    case 'datetime':
      return formatInstant(value, locale);
    case 'number':
      return Number(value).toLocaleString(locale);
    case 'bool':
      return value === 'true' ? t('common.yes') : t('common.no');
    case 'enum':
      return vocabLabel(t, column.vocab, value);
    default:
      return value;
  }
}

function contractStatusLabel(t: Translate, status: string): string {
  if (status === 'draft') return t('contract.status.draft');
  if (status === 'active') return t('contract.status.active');
  if (status === 'closed') return t('contract.status.closed');
  return status;
}

/** An enum cell's words. ⚠️ One `t()` per branch with a literal key — the catalogue check reads only those. */
function vocabLabel(t: Translate, vocab: ReportListColumn['vocab'], value: string): string {
  if (vocab === 'contractStatus') return contractStatusLabel(t, value);
  if (vocab === 'contractType') {
    if (value === 'recurring') return t('contract.type.recurring');
    if (value === 'milestone') return t('contract.type.milestone');
    if (value === 'service') return t('contract.type.service');
  }
  if (vocab === 'lineItemType') {
    if (value === 'fixed') return t('lineItem.type.fixed');
    if (value === 'time_and_material') return t('lineItem.type.timeAndMaterial');
    if (value === 'license') return t('lineItem.type.license');
    if (value === 'non_billable') return t('lineItem.type.nonBillable');
  }
  if (vocab === 'lineItemScope') {
    if (value === 'retainer') return t('lineItem.scope.retainer');
    if (value === 'billable') return t('lineItem.scope.billable');
  }
  if (vocab === 'booking') {
    if (value === 'hard') return t('resource.cell.hard');
    if (value === 'soft') return t('resource.cell.soft');
  }
  if (vocab === 'lineItemStatus') {
    if (value === 'open') return t('report.vocab.lineItemOpen');
    if (value === 'closed') return t('report.vocab.lineItemClosed');
  }
  return value;
}

export function filterLabel(
  t: Translate,
  key: ReportFilterKey,
  source: ReportDefinition['source'],
): string {
  if (key === 'projects') return t('report.filter.projects');
  if (key === 'clients') return t('report.filter.clients');
  if (key === 'members') {
    if (source === 'worklog') return t('report.filter.loggedBy');
    if (source === 'task') return t('report.filter.assignee');
    /* 17 Oct 2026 · Munkaidő: whose working days. */
    if (source === 'workday') return t('report.timeline.person');
    /* 26 Oct 2026 · whose allocations. */
    if (source === 'allocation') return t('report.timeline.person');
    return t('report.filter.owner');
  }
  /* R-60 · the team of whoever the members filter names — who logged, the assignee, whose days. */
  if (key === 'teams') return t('report.filter.teams');
  if (key === 'contracts') return t('report.filter.contracts');
  if (key === 'lineItems') return t('report.filter.lineItems');
  if (key === 'priorities') return t('report.filter.priorities');
  return t('report.filter.statuses');
}

/**
 * A catalogue column's heading.
 *
 * ⚠️ **One `t()` per key, literal.** `t(\`report.col.${key}\`)` would be invisible to the catalogue
 * check and every entry would read as an orphan.
 */
export function reportColumnLabel(t: Translate, key: string): string {
  switch (key) {
    case 'workedOn':
      return t('report.col.workedOn');
    case 'hours':
      return t('report.col.hours');
    case 'member':
      return t('report.col.member');
    case 'team':
      return t('report.col.team');
    /* 26 Oct 2026 · the allocation source's own two. */
    case 'person':
      return t('report.timeline.person');
    case 'booking':
      return t('resource.cell.booking');
    case 'note':
      return t('report.col.note');
    case 'enteredAt':
      return t('report.col.enteredAt');
    case 'overtimeType':
      return t('report.col.overtimeType');
    case 'cost':
      return t('report.col.cost');
    case 'targetValue':
      return t('report.col.targetValue');
    case 'taskKey':
      return t('report.col.taskKey');
    case 'taskTitle':
      return t('report.col.taskTitle');
    case 'taskDescription':
      return t('report.col.taskDescription');
    case 'taskStatus':
      return t('report.col.taskStatus');
    case 'taskPriority':
      return t('report.col.taskPriority');
    case 'taskAssignee':
      return t('report.col.taskAssignee');
    case 'taskEstimate':
      return t('report.col.taskEstimate');
    case 'taskStartsOn':
      return t('report.col.taskStartsOn');
    case 'taskDueOn':
      return t('report.col.taskDueOn');
    case 'taskComponents':
      return t('report.col.taskComponents');
    case 'taskFixVersions':
      return t('report.col.taskFixVersions');
    case 'taskEpic':
      return t('report.col.taskEpic');
    case 'taskLogged':
      return t('report.col.taskLogged');
    case 'taskRemaining':
      return t('report.col.taskRemaining');
    case 'taskCreatedAt':
      return t('report.col.taskCreatedAt');
    case 'taskUpdatedAt':
      return t('report.col.taskUpdatedAt');
    case 'project':
      return t('report.col.project');
    case 'client':
      return t('report.col.client');
    case 'contract':
      return t('report.col.contract');
    case 'lineItem':
      return t('report.col.lineItem');
    case 'contractCode':
      return t('report.col.contractCode');
    case 'contractName':
      return t('report.col.contractName');
    case 'contractType':
      return t('report.col.contractType');
    case 'contractStatus':
      return t('report.col.contractStatus');
    case 'owner':
      return t('report.col.owner');
    case 'startsOn':
      return t('report.col.startsOn');
    case 'endsOn':
      return t('report.col.endsOn');
    case 'lineItemCount':
      return t('report.col.lineItemCount');
    case 'estimate':
      return t('report.col.estimate');
    case 'budget':
      return t('report.col.budget');
    case 'revenue':
      return t('report.col.revenue');
    case 'invoiced':
      return t('report.col.invoiced');
    case 'lineItemNumber':
      return t('report.col.lineItemNumber');
    case 'lineItemType':
      return t('report.col.lineItemType');
    case 'lineItemScope':
      return t('report.col.lineItemScope');
    case 'lineItemStatus':
      return t('report.col.lineItemStatus');
    case 'closedAt':
      return t('report.col.closedAt');
    case 'syncTarget':
      return t('report.col.syncTarget');
    case 'rate':
      return t('report.col.rate');
    case 'periodFrom':
      return t('report.col.periodFrom');
    case 'periodTo':
      return t('report.col.periodTo');
    default:
      return key;
  }
}
